import axiosInstance from '@/services/axiosInstance';
import { request } from '@/services/request';
import type { GenericErrorResponse } from '@/types/api.types';
import type {
  CourtCreateRequest,
  CourtGetAllParams,
  CourtGetAllResponse,
  CourtMessageResponse,
  CourtResponse,
  CourtUpdateRequest,
} from '@/types/court.types';
import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';

export const fetchCourts = (params?: CourtGetAllParams) =>
  request(() => axiosInstance.get<CourtGetAllResponse>('/courts/', { params }));

/** `options.query.queryKey`, when given, replaces the params in the cache key (DataTable passes its own). */
export const useCourts = (
  params?: CourtGetAllParams,
  options?: { query?: Partial<UseQueryOptions<CourtGetAllResponse, GenericErrorResponse>> }
) => {
  const { queryKey, ...queryOptions } = options?.query ?? {};
  return useQuery<CourtGetAllResponse, GenericErrorResponse>({
    ...queryOptions,
    queryKey: ['courts', ...(queryKey ?? [params])],
    queryFn: () => fetchCourts(params),
  });
};

export const useCourt = (courtId: string | undefined) =>
  useQuery<CourtResponse, GenericErrorResponse>({
    queryKey: ['court', courtId],
    queryFn: () => request(() => axiosInstance.get<CourtResponse>(`/courts/${encodeURIComponent(courtId!)}`)),
    enabled: !!courtId,
  });

export const useCreateCourt = () =>
  useMutation<CourtMessageResponse, GenericErrorResponse, { data: CourtCreateRequest }>({
    mutationFn: ({ data }) => request(() => axiosInstance.post<CourtMessageResponse>('/courts/', data)),
  });

export const useUpdateCourt = () =>
  useMutation<CourtMessageResponse, GenericErrorResponse, { id: string; data: CourtUpdateRequest }>({
    mutationFn: ({ id, data }) =>
      request(() => axiosInstance.put<CourtMessageResponse>(`/courts/${encodeURIComponent(id)}`, data)),
  });
